/**
 * useFilterPresets Hook
 *
 * Encapsulates all filter preset logic for DRE dashboard and drill-down pages.
 * Manages state, localStorage persistence, and provides a clean API for
 * saving, loading, deleting, and applying filter presets.
 *
 * Usage:
 * ```tsx
 * const {
 *   presets,
 *   isModalOpen,
 *   openModal,
 *   closeModal,
 *   savePreset,
 *   deletePreset,
 *   checkNameExists,
 * } = useFilterPresets();
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import * as filterPresetService from '../services/filterPresetService';
import type { FilterPreset, FilterPresetFilters } from '../services/filterPresetService';

// ============================================================================
// TYPES
// ============================================================================

export interface UseFilterPresetsReturn {
  /** List of saved presets */
  presets: FilterPreset[];
  /** Whether the save modal is open */
  isModalOpen: boolean;
  /** Open the save modal */
  openModal: () => void;
  /** Close the save modal */
  closeModal: () => void;
  /** Save a new preset or update existing */
  savePreset: (name: string, filters: FilterPresetFilters) => FilterPreset;
  /** Delete a preset by ID */
  deletePreset: (presetId: string) => boolean;
  /** Check if a preset name already exists */
  checkNameExists: (name: string) => boolean;
  /** Refresh presets from localStorage */
  refreshPresets: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useFilterPresets(): UseFilterPresetsReturn {
  // State
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load presets on mount
  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = useCallback(() => {
    const loaded = filterPresetService.getPresets();
    setPresets(loaded);
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const savePreset = useCallback(
    (name: string, filters: FilterPresetFilters): FilterPreset => {
      const saved = filterPresetService.savePreset(name, filters);
      loadPresets(); // Refresh list after save
      return saved;
    },
    [loadPresets]
  );

  const deletePreset = useCallback(
    (presetId: string): boolean => {
      const deleted = filterPresetService.deletePreset(presetId);
      if (deleted) {
        loadPresets(); // Refresh list after delete
      }
      return deleted;
    },
    [loadPresets]
  );

  const checkNameExists = useCallback((name: string): boolean => {
    return filterPresetService.presetNameExists(name);
  }, []);

  const refreshPresets = useCallback(() => {
    loadPresets();
  }, [loadPresets]);

  return {
    presets,
    isModalOpen,
    openModal,
    closeModal,
    savePreset,
    deletePreset,
    checkNameExists,
    refreshPresets,
  };
}

export default useFilterPresets;
